import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/analytics";
import { useTodayLive, liveMode } from "../lib/useTodayLive";
import { addAllToGoogleCalendar } from "../lib/calendar";
import LastDropCard from "../components/LastDropCard";

/* Drop #001 launches 15 Aug 2026, 21:00 IST → 15:30 UTC */
const DROP_ONE_UTC = "2026-08-15T15:30:00Z";
const SEASON_ONE = [
  ["#001", "Tech"], ["#002", "Home"], ["#003", "Kitchen"],
  ["#004", "Grooming"], ["#005", "Fitness"], ["#006", "Audio"],
  ["#007", "Travel"], ["#008", "Wellness"], ["#009", "Desk"],
  ["#010", "Style"], ["#011", "Kids"], ["#012", "Outdoors"],
  ["#013", "Reading"], ["#014", "Craft"], ["#015", "Mega Finale"],
];

const FAQ = [
  ["When does Season One start?",
   "Drop #001 goes live 15 August 2026 at 9:00 PM IST. Then one drop every night for 15 nights until 29 August."],
  ["How does a live drop work?",
   "Every night at 9 PM one product goes live for exactly 2 minutes. As more members join, the price crashes through 5 milestones. When the timer ends you have a short window to lock in the final price. Miss the live, miss the deal."],
  ["Why only 250 Founding Members?",
   "We're launching intentionally small. The first 250 members shape the community, unlock the first drops, and keep their Founding badge forever."],
  ["Can I invite my friends?",
   "Yes — every Founding Member gets 3 exclusive invite codes. Share your DSL### link (or copy it from the Invites card on this page) and the first 3 people you invite lock in Founding numbers too."],
  ["How do I get reminded before a drop?",
   "Use the 'Add to Calendar' card on this page. One click adds all 15 drops to Google Calendar with a 10-minute pre-live reminder."],
  ["I need help — how do I reach you?",
   "Email us at hello@dropshop.live. We read every message."],
];

/**
 * Add all 15 Season One drops to the user's Google Calendar in one click.
 * (moved to lib/calendar.js — kept comment as a signpost for greppers.)
 */

export default function Home() {
  const { user, fetchUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [seasonSlots, setSeasonSlots] = useState(null);
  const [lastBuyable, setLastBuyable] = useState(null);
  const [defaultAddr, setDefaultAddr] = useState(null);

  useEffect(() => {
    track("home_view", { member_no: user?.member_no });
    // Notification-click: user arrived via a calendar reminder link (?ref=cal)
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("ref") === "cal") {
        track("notification_click", { source: "calendar" });
      }
    } catch (_e) { /* noop */ }
    // Post-signup deep-scroll: AuthCallback redirects new members to
    // `/#season-one` so they land directly on the Season One drop schedule
    // instead of the welcome hero. React Router doesn't auto-scroll to hash
    // anchors — we do it here after the layout paints.
    if (window.location.hash === "#season-one") {
      requestAnimationFrame(() => {
        document.getElementById("season-one")?.scrollIntoView({ behavior: "smooth", block: "start" });
        try { track("signup_landed_on_schedule"); } catch (_e) { /* noop */ }
      });
    }
    api.get("/waitlist/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/lives/season").then((r) => setSeasonSlots(r.data?.slots || [])).catch(() => {});
    api.get("/lives/last-buyable").then((r) => setLastBuyable(r.data)).catch(() => {});
    api.get("/addresses").then((r) => {
      const list = r.data || [];
      setDefaultAddr(list.find((a) => a.is_default) || list[0] || null);
    }).catch(() => {});
    const id = setInterval(() => api.get("/waitlist/stats").then((r) => setStats(r.data)).catch(() => {}), 20000);
    const bid = setInterval(
      () => api.get("/lives/last-buyable").then((r) => setLastBuyable(r.data)).catch(() => {}),
      60000,
    );

    // Home dwell + session_end tracking
    const enteredAt = Date.now();
    const flushDwell = (reason) => {
      const dwell_ms = Date.now() - enteredAt;
      if (dwell_ms < 500) return; // ignore instant unmounts
      track("home_dwell_ms", { ms: dwell_ms, reason });
      track("session_end", { on: "home", ms: dwell_ms, reason });
    };
    const onVis = () => { if (document.visibilityState === "hidden") flushDwell("visibility"); };
    const onHide = () => flushDwell("pagehide");
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onHide);
    return () => {
      clearInterval(id);
      clearInterval(bid);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onHide);
      flushDwell("route_change");
    };
  }, [user?.member_no]);

  const firstName = (user?.name || user?.email || "").split(" ")[0].split("@")[0];
  const memberNo = user?.member_no || 0;
  const padded = String(memberNo).padStart(3, "0");
  const subscribed = Boolean(user?.notify_preferences?.calendar_subscribed);

  // One-shot post-signup welcome toast. AuthCallback sets `welcome_toast=1`
  // in sessionStorage; we read + clear it on mount and pop a soft top-right
  // toast for 5s. The CTA fires the same shared Google-calendar helper.
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem("welcome_toast") === "1") {
        sessionStorage.removeItem("welcome_toast");
        setShowWelcome(true);
        try { track("welcome_toast_shown", { member_no: memberNo }); } catch (_e) { /* noop */ }
        const t = setTimeout(() => setShowWelcome(false), 5000);
        return () => clearTimeout(t);
      }
    } catch (_e) { /* noop */ }
    return undefined;
  }, [memberNo]);

  // One-shot "You've been promoted from the waitlist" modal. Fires when
  // /auth/me returns `promotion_notice_pending: true` (set by
  // core.founding.promote_next_from_waitlist). The client shows the modal
  // once, POSTs /auth/me/promotion-seen to clear the server-side flag, and
  // never shows it again for this user.
  const [showPromotion, setShowPromotion] = useState(false);
  useEffect(() => {
    if (user?.promotion_notice_pending && !showPromotion) {
      setShowPromotion(true);
      try { track("promotion_modal_shown", { member_no: memberNo }); } catch (_e) { /* noop */ }
    }
  }, [user?.promotion_notice_pending, memberNo, showPromotion]);

  const dismissPromotion = async (reason) => {
    setShowPromotion(false);
    try { track("promotion_modal_dismissed", { member_no: memberNo, reason }); } catch (_e) { /* noop */ }
    try { await api.post("/auth/me/promotion-seen", {}); } catch (_e) { /* noop */ }
    if (fetchUser) await fetchUser();
  };
  const promotionCta = async () => {
    await dismissPromotion("cta");
    // Scroll to Season One
    requestAnimationFrame(() => {
      document.getElementById("season-one")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#161616]" data-testid="clubhouse">
      {/* Post-signup welcome toast — top-right, auto-dismisses after 5s */}
      {showWelcome && (
        <div
          className="fixed z-50 animate-welcome-in"
          style={{ top: "calc(env(safe-area-inset-top) + 96px)", right: "16px" }}
          data-testid="welcome-toast"
        >
          <div className="border-2 border-[#161616] bg-[#FFFFFF] shadow-[4px_4px_0_#264733] px-4 py-3 flex items-center gap-3 max-w-sm">
            <span className="text-2xl leading-none">🌟</span>
            <div className="leading-tight">
              <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#264733] font-bold">
                You&apos;re in · {user?.label || "Founding Member"}
              </div>
              <div className="font-display text-lg tracking-tight leading-none tabular-nums mt-0.5">
                You are Member <span className="text-[#FF6A00]">#{padded}</span>
              </div>
              <button
                onClick={async () => {
                  try { track("welcome_toast_cta", { member_no: memberNo }); } catch (_e) { /* noop */ }
                  await addAllToGoogleCalendar("welcome_toast");
                  setShowWelcome(false);
                  if (fetchUser) await fetchUser();
                }}
                className="mt-2 font-mono-ds text-[11px] uppercase tracking-[0.20em] text-[#FF6A00] hover:text-[#161616] font-bold inline-flex items-center gap-1"
                data-testid="welcome-toast-cta"
              >
                Save your spot →
              </button>
            </div>
            <button
              onClick={() => setShowWelcome(false)}
              className="ml-2 w-6 h-6 border border-[#E5E1D8] hover:border-[#161616] flex items-center justify-center text-sm leading-none text-[#6E6E6E] hover:text-[#161616]"
              aria-label="Dismiss"
              data-testid="welcome-toast-dismiss"
            >×</button>
          </div>
        </div>
      )}

      {/* Auto-promotion celebration modal — shown once when a waitlist user
          gets promoted into the room (server-side flag promotion_notice_pending). */}
      {showPromotion && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 animate-welcome-in"
          data-testid="promotion-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-[#161616]/70 backdrop-blur-sm" onClick={() => dismissPromotion("backdrop")} />
          <div className="relative border-2 border-[#161616] bg-[#FFFFFF] shadow-[8px_8px_0_#FF6A00] p-8 md:p-10 max-w-lg w-full">
            <div className="flex items-start justify-between gap-4">
              <div className="brand-tag">// Access Granted</div>
              <button
                onClick={() => dismissPromotion("close")}
                className="w-7 h-7 border border-[#E5E1D8] hover:border-[#161616] flex items-center justify-center text-sm leading-none text-[#6E6E6E] hover:text-[#161616]"
                aria-label="Close"
                data-testid="promotion-modal-close"
              >×</button>
            </div>
            <div className="mt-4 text-6xl leading-none">🎉</div>
            <h2 className="mt-4 font-display uppercase tracking-tight leading-[0.95] text-4xl md:text-5xl">
              A spot <span className="text-outline">opened up.</span>
            </h2>
            <p className="mt-5 text-[#6E6E6E] text-base md:text-lg leading-relaxed">
              You&rsquo;ve been lifted from the waitlist into the room. Welcome, {firstName || "Founder"} — you are now <span className="text-[#161616] font-semibold">{user?.label || "in"}</span> as Member <span className="text-[#FF6A00] font-bold">#{padded}</span>.
            </p>
            <div className="mt-6 border-t border-[#E5E1D8] pt-5 font-mono-ds text-[11px] uppercase tracking-widest text-[#6E6E6E]" data-testid="promotion-modal-body">
              <span className="text-[#264733] font-bold">◆</span> You now have access to every Season One drop · 9 PM IST · 2 minutes only.
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={promotionCta}
                className="btn-urgent h-11 px-6 flex-1"
                data-testid="promotion-modal-cta"
              >
                Take me to Season One →
              </button>
              <button
                onClick={() => dismissPromotion("later")}
                className="btn-outline-ns h-11 px-6"
                data-testid="promotion-modal-later"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============== HERO WELCOME ============== */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-8" data-testid="clubhouse-welcome">
        <div className={`grid gap-8 ${lastBuyable ? "lg:grid-cols-12 items-start" : ""}`}>
          <div className={lastBuyable ? "lg:col-span-7" : ""}>
            <div className="brand-tag mb-3">// Founding Member Access</div>
            <h1 className="font-display uppercase leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl">
              Welcome, <span className="text-outline">{firstName || "Founder"}.</span>
            </h1>
            <div className="mt-6 inline-flex items-center gap-3 border-2 border-[#264733] bg-[#B9E641]/30 px-4 py-2.5 shadow-[3px_3px_0_#161616]" data-testid="clubhouse-badge">
              <span className="text-2xl leading-none">{user?.emoji || "🌟"}</span>
              <div className="leading-tight">
                <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#264733] font-bold">
                  {user?.label || "Founding Member"}
                </div>
                <div className="font-display text-xl tracking-tight leading-none tabular-nums">
                  No. {padded}
                </div>
              </div>
            </div>
            <p className="mt-6 max-w-xl text-[#6E6E6E] leading-relaxed">
              You&rsquo;re one of only <span className="text-[#161616] font-semibold">250 Founding Members</span> helping build India&rsquo;s first daily shopping event. See you at 9 PM.
            </p>
          </div>

          {lastBuyable && (
            <div className="lg:col-span-5" data-testid="hero-last-drop-slot">
              <LastDropCard
                drop={lastBuyable}
                isAuthed={!!user}
                defaultAddr={defaultAddr}
                compact
              />
            </div>
          )}
        </div>
      </section>

      {/* ============== NEXT DROP CARD ============== */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-10" data-testid="clubhouse-next-drop">
        <NextDropCard onSubscribed={fetchUser} />
      </section>

      {/* ============== DAILY RITUAL TIMELINE ============== */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="clubhouse-ritual">
        <div className="brand-tag mb-3">// The Daily Ritual · 002</div>
        <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
          Every day, <span className="text-outline">on cue.</span>
        </h2>
        <div className="mt-8 grid md:grid-cols-5 gap-3">
          {[
            ["10:00 AM", "Product Revealed", "🎁"],
            ["8:50 PM", "Reminder Notification", "🔔"],
            ["9:00 PM", "Live Starts", "🎬"],
            ["9:02 PM", "Checkout Closes", "🔒"],
            ["Tomorrow", "It Begins Again", "🌅"],
          ].map(([time, title, icon]) => (
            <div key={title} className="border border-[#161616] bg-[#FFFFFF] p-4 shadow-[2px_2px_0_#161616] flex flex-col gap-1.5 min-h-[130px]">
              <span className="text-2xl leading-none">{icon}</span>
              <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#264733] font-bold mt-1">{time}</div>
              <div className="font-display text-base uppercase tracking-tight leading-tight">{title}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============== SEASON ONE ============== */}
      <section id="season-one" className="border-y border-[#161616] bg-[#FFFFFF] px-6 md:px-10 py-12 md:py-16 scroll-mt-24" data-testid="clubhouse-season">
        <div className="max-w-5xl mx-auto">
          <div className="brand-tag mb-3">// Season One · 003</div>
          <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
            15 Consecutive <span className="text-outline">Daily Drops.</span>
          </h2>
          <div className="mt-8 flex gap-3 overflow-x-auto pb-4 -mx-6 md:-mx-10 px-6 md:px-10 snap-x scroll-smooth">
            {(seasonSlots || SEASON_ONE.map(([d, t], i) => ({
              drop_no: i + 1, category: t, date_ist: `2026-08-${String(15 + i).padStart(2, "0")}`,
            }))).map((slot, i) => {
              const isFinale = slot.drop_no === 15;
              const dropLabel = `#${String(slot.drop_no).padStart(3, "0")}`;
              const day = new Date(slot.date_ist + "T00:00:00").getDate();
              const scheduled = !!slot.live_id;
              // Prefer the product's own inventory category once a drop has a
              // product attached; fall back to the season-schedule category
              // for empty slots so the grid always renders a label.
              const categoryLabel = slot.product_category || slot.category;
              const cardClass = `shrink-0 w-40 snap-start border p-4 flex flex-col gap-1.5 min-h-[130px] relative ${isFinale ? "border-[#161616] bg-[#B9E641] shadow-[3px_3px_0_#161616]" : "border-[#161616] bg-[#F8F7F2] shadow-[2px_2px_0_#161616]"} ${scheduled ? "hover:-translate-x-[1px] hover:-translate-y-[1px] transition-transform cursor-pointer" : ""}`;
              const inner = (
                <>
                  <div className="brand-tiny text-[#6E6E6E]">{day} Aug · 9 PM</div>
                  <div className="font-display text-xl uppercase tracking-tight leading-none">Drop {dropLabel}</div>
                  <div
                    className={`font-mono-ds text-[11px] uppercase tracking-widest ${isFinale ? "text-[#161616] font-bold" : "text-[#264733] font-bold"}`}
                    data-testid={`clubhouse-drop-${String(slot.drop_no).padStart(3, "0")}-category`}
                  >
                    {categoryLabel}
                  </div>
                  <div className="mt-auto font-mono-ds text-[9px] uppercase tracking-[0.24em]">
                    {scheduled ? <span className="text-[#264733]">● Scheduled</span> : <span className="text-[#6E6E6E]">◇ Pending</span>}
                  </div>
                  {scheduled && (
                    <span className="absolute top-3 right-3 font-mono-ds text-[14px] leading-none text-[#264733]" aria-hidden="true">→</span>
                  )}
                </>
              );
              const testId = `clubhouse-drop-${String(slot.drop_no).padStart(3, "0")}`;
              return scheduled ? (
                <Link key={slot.drop_no} to={`/live/${slot.live_id}`} className={cardClass} data-testid={testId}>
                  {inner}
                </Link>
              ) : (
                <div key={slot.drop_no} className={cardClass} data-testid={testId}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== COMMUNITY + INVITE ============== */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16 grid md:grid-cols-2 gap-5">
        <CommunityCard stats={stats} />
        <InviteCard memberNo={memberNo} />
      </section>

      {/* ============== INVITE IMPACT (only rendered when refs > 0) ============== */}
      <InviteImpactLedger memberNo={memberNo} foundingCap={stats?.founding_cap ?? 250} />

      {/* ============== FOUNDER MESSAGE ============== */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="clubhouse-founder">
        <div className="brand-tag mb-3">// From the Desk · 006</div>
        <div className="grid md:grid-cols-12 gap-6 items-start">
          <div className="md:col-span-4">
            <div className="aspect-square border-2 border-[#161616] shadow-[6px_6px_0_#264733] overflow-hidden bg-[#F0EDE4] flex items-center justify-center">
              <div className="font-display text-7xl text-[#264733] leading-none">S</div>
            </div>
            <div className="mt-3 font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#6E6E6E]">
              Shubham · Founder
            </div>
          </div>
          <div className="md:col-span-8 font-display text-xl md:text-2xl leading-snug tracking-tight">
            &ldquo;Thank you for believing in Dropshop Live before anyone else. The first <span className="italic text-[#264733]">250 members</span> are more than early users — they&rsquo;re helping build India&rsquo;s first daily shopping ritual. Every piece of feedback matters, and every Drop will get better because of this community. <span className="italic text-[#264733]">See you at 9 PM.</span>&rdquo;
          </div>
        </div>
      </section>

      {/* ============== LAUNCH PROGRESS ============== */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16 border-t border-[#E5E1D8]" data-testid="clubhouse-progress">
        <div className="brand-tag mb-3">// Your Journey · 007</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl leading-[0.95] tracking-tight">
          Countdown to <span className="text-outline">Drop #001.</span>
        </h2>
        <ul className="mt-8 space-y-2.5" data-testid="progress-list">
          <ProgressStep done label="Joined Dropshop Live" />
          <ProgressStep done label="Founding Member Confirmed" />
          <ProgressStep label="Product Reveal (Aug 15 · 10 AM IST)" />
          <ProgressStep label="First Live (Aug 15 · 9 PM IST)" />
          <ProgressStep label="First Purchase" />
        </ul>
      </section>

      {/* ============== NOTIFICATIONS ============== */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="clubhouse-notifs">
        <NotificationCard subscribed={subscribed} subscribedAt={user?.notify_preferences?.subscribed_at} onSubscribed={fetchUser} />
      </section>

      {/* ============== FAQ ============== */}
      <section id="faq" className="border-t border-[#E5E1D8] bg-[#FFFFFF] px-6 md:px-10 py-16 md:py-20" data-testid="clubhouse-faq">
        <div className="max-w-4xl mx-auto">
          <div className="brand-tag mb-3">// FAQ · 009</div>
          <h2 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight mb-6">
            Questions, <span className="text-outline">Answered.</span>
          </h2>
          <div className="border-t border-[#161616]">
            {FAQ.map(([q, a], i) => <FaqItem key={i} idx={i} q={q} a={a} />)}
          </div>
        </div>
      </section>

      {/* ============== FOOTER ============== */}
      <footer className="border-t border-[#161616] px-6 md:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-3 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]" data-testid="clubhouse-footer">
        <span>© 2026 · Drop Shop Live · One product a night</span>
        <div className="flex items-center gap-4 flex-wrap justify-center">
          <a href="/profile" className="hover:text-[#FF6A00]" data-testid="footer-profile">Profile</a>
          <a href="#faq" onClick={(e) => { e.preventDefault(); document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" }); }} className="hover:text-[#FF6A00]" data-testid="footer-faq">FAQ</a>
          <a href="mailto:hello@dropshop.live" className="hover:text-[#FF6A00] normal-case tracking-normal" data-testid="footer-contact">hello@dropshop.live</a>
        </div>
      </footer>
    </div>
  );
}

/* ------------ Next Drop card ------------
 * Mode-switches on the polled `/api/lives/today` payload:
 *   - live / final_window → red pulsing "LIVE NOW · Join" CTA (routes to /live/:id)
 *   - open (preshow)      → green "Doors Open · Take Your Seat" CTA (routes to /live/:id)
 *   - scheduled today     → countdown to today's drop
 *   - scheduled future    → countdown to that drop (drop_no in badge)
 *   - idle (nothing set)  → fallback countdown to Drop #001 launch
 */
function NextDropCard({ onSubscribed }) {
  const nav = useNavigate();
  const { live } = useTodayLive({ intervalMs: 15000 });
  const mode = liveMode(live);
  const isJoinable = mode === "live" || mode === "final" || mode === "preshow" || mode === "preopen";

  // Auto-enter the LiveRoom the moment the host presses Start — no click
  // required. We only auto-nav on `live` (not `final`) so members already
  // browsing the site are pulled in exactly once, when the drop actually
  // starts. sessionStorage guard prevents re-navigation if the user backed out.
  useEffect(() => {
    if (mode !== "live" || !live?.live_id) return;
    const key = `auto_nav_live_${live.live_id}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* noop */ }
    nav(`/live/${live.live_id}`, { replace: true });
  }, [mode, live?.live_id, nav]);

  // Countdown target: live scheduled_at when present, else Drop #001 launch date.
  const targetIso = live?.scheduled_at || DROP_ONE_UTC;
  const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  const dropNo = live?.drop_no ? String(live.drop_no).padStart(3, "0") : "001";
  const category = live?.product?.category || null;
  const viewerCount = live?.participants || 0;
  const dateLabel = live?.scheduled_at
    ? new Date(live.scheduled_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" }) + " · 9:00 PM IST"
    : "15 Aug · 9:00 PM IST";

  // Reveal time = 10 AM IST on the drop's day = scheduled_at (9 PM IST) - 11 hours.
  // Once `now >= revealAt` AND the product has an image, we swap the `?` silhouette
  // for the actual product photo. Before that we still show the silhouette + reveal chip.
  const revealAt = live?.scheduled_at ? new Date(live.scheduled_at).getTime() - 11 * 3600 * 1000 : null;
  const productImage = live?.product?.image_url;
  const isRevealed = !!(revealAt && now >= revealAt && productImage);
  const revealDateLabel = live?.scheduled_at
    ? new Date(live.scheduled_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" })
    : "15 Aug";

  // -------- LIVE-NOW variant (red, pulsing) --------
  if (isJoinable && live?.live_id) {
    const barLabel = mode === "live" ? "● LIVE NOW"
      : mode === "final" ? "◆ FINAL WINDOW · CHECKOUT CLOSING"
      : mode === "preshow" ? "◇ DOORS OPEN · PRESHOW"
      : "◇ CHAT OPEN · WAITING ROOM";
    const cta = mode === "final" ? "Lock My Price →"
      : mode === "preopen" ? "Enter Waiting Room →"
      : "Join The Drop →";
    return (
      <Link
        to={`/live/${live.live_id}`}
        className="block border-2 border-[#FF3B30] bg-[#161616] text-white shadow-[8px_8px_0_#FF3B30] p-6 md:p-10 hover:translate-x-[-1px] hover:translate-y-[-1px] transition-transform"
        onClick={() => { try { track("home_join_live_click", { live_id: live.live_id, drop_no: live.drop_no, mode }); } catch (_e) { /* noop */ } }}
        data-testid="next-drop-join-now"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 font-mono-ds text-[11px] uppercase tracking-[0.28em] text-[#FF3B30] font-bold">
            <span className={`inline-block w-2.5 h-2.5 rounded-full bg-[#FF3B30] ${mode === "live" ? "animate-pulse" : ""}`} />
            {barLabel}
          </div>
          <div className="brand-tiny text-white/60">Drop #{dropNo}{category ? ` · ${category}` : ""}</div>
        </div>
        <div className="font-display uppercase leading-[0.95] tracking-tight text-4xl md:text-5xl">
          {live?.title || "Tonight's drop is on."}
        </div>
        {live?.product?.name && (
          <div className="mt-2 font-mono-ds text-sm text-white/70 uppercase tracking-widest">
            {live.product.name}
          </div>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span className="btn-urgent h-12 px-6 flex items-center gap-2 pointer-events-none" data-testid="next-drop-join-cta">
            {cta}
          </span>
          {viewerCount > 0 && (
            <span className="font-mono-ds text-[11px] uppercase tracking-[0.24em] text-white/80" data-testid="next-drop-viewer-count">
              {viewerCount.toLocaleString("en-IN")} member{viewerCount === 1 ? "" : "s"} inside
            </span>
          )}
        </div>
      </Link>
    );
  }

  // -------- COUNTDOWN variant (dark cream, existing look) --------
  return (
    <div className="border-2 border-[#161616] bg-[#FFFFFF] shadow-[8px_8px_0_#264733] p-6 md:p-10">
      <div className="flex items-center justify-between mb-6">
        <div className="brand-pill" data-testid="next-drop-tag">● Drop #{dropNo}</div>
        <div className="brand-tiny text-[#6E6E6E]">{dateLabel}</div>
      </div>
      <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-center">
        {/* Product teaser — silhouette before reveal, real product image after 10 AM IST reveal */}
        <div className="md:col-span-5">
          <div className="aspect-[4/3] border border-[#161616] bg-gradient-to-br from-[#F0EDE4] to-[#E5E1D8] flex items-center justify-center relative overflow-hidden" data-testid="next-drop-teaser">
            {isRevealed ? (
              <img
                src={productImage}
                alt={live?.product?.name || "Tonight's drop"}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="next-drop-product-image"
              />
            ) : (
              <span className="font-display text-7xl md:text-8xl text-[#161616]/12 leading-none select-none">?</span>
            )}
            <div className="absolute bottom-3 left-3 brand-pill-accent">
              {isRevealed
                ? (live?.product?.name || category || "Revealed")
                : category
                  ? category
                  : `Reveal · ${revealDateLabel} · 10 AM`}
            </div>
          </div>
        </div>
        {/* Countdown + info */}
        <div className="md:col-span-7">
          <div className="brand-tiny text-[#6E6E6E]">Starts In</div>
          <div className="mt-2 flex flex-wrap gap-2 md:gap-3" data-testid="next-drop-countdown">
            {[["Days", days], ["Hours", hours], ["Minutes", mins], ["Seconds", secs]].map(([l, v], i) => (
              <div key={l} className={`border border-[#161616] bg-[#F8F7F2] px-3 md:px-4 py-2 md:py-3 min-w-[60px] md:min-w-[80px] flex flex-col items-center shadow-[2px_2px_0_#161616] ${i === 3 ? "text-[#FF6A00]" : ""}`}>
                <span className="font-display text-2xl md:text-4xl leading-none tabular-nums">{String(v).padStart(2, "0")}</span>
                <span className="font-mono-ds text-[9px] md:text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E] mt-1">{l}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4 font-mono-ds text-[11px] uppercase tracking-[0.20em] text-[#6E6E6E]">
            <span>📅 Every Day</span>
            <span>🕘 9:00 PM IST</span>
            <span>⏱ Lasts 2 minutes</span>
          </div>

          {/* Social-proof nudge — only when a scheduled live already has
              members warming up the waiting room. Routes into the PreLive page. */}
          {viewerCount > 0 && live?.live_id && (
            <Link
              to={`/live/${live.live_id}`}
              className="mt-4 inline-flex items-center gap-2 border border-[#264733] bg-[#B9E641]/40 px-3 py-2 font-mono-ds text-[11px] uppercase tracking-[0.20em] text-[#264733] hover:bg-[#B9E641]/70 transition-colors"
              onClick={() => { try { track("home_socialproof_click", { live_id: live.live_id, drop_no: live.drop_no, participants: viewerCount }); } catch (_e) { /* noop */ } }}
              data-testid="next-drop-socialproof-pill"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#264733] animate-pulse" />
              <span className="tabular-nums font-bold">{viewerCount.toLocaleString("en-IN")}</span>
              <span>member{viewerCount === 1 ? "" : "s"} already in the waiting room</span>
              <span className="ml-1">→</span>
            </Link>
          )}

          <button
            onClick={async () => {
              await addAllToGoogleCalendar("next_drop_card");
              if (onSubscribed) await onSubscribed();
            }}
            className="btn-urgent mt-6 h-12 px-6"
            data-testid="next-drop-notify"
          >
            Notify Me →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------ Community card ------------ */
function CommunityCard({ stats }) {
  const total = stats?.total_signups ?? 0;
  const cap = stats?.founding_cap ?? 250;
  const left = stats?.spots_left ?? cap;
  const pct = Math.min(100, Math.round((total / cap) * 100));
  return (
    <div className="border-2 border-[#161616] bg-[#FFFFFF] shadow-[4px_4px_0_#264733] p-6" data-testid="clubhouse-community">
      <div className="brand-tag mb-2">// Community · 004</div>
      <div className="font-display text-2xl uppercase tracking-tight leading-tight">
        👥 Founding <span className="text-outline">Members.</span>
      </div>
      <div className="mt-5 flex items-baseline justify-between">
        <div>
          <div className="brand-tiny text-[#6E6E6E]">Joined</div>
          <div className="font-display text-4xl md:text-5xl tabular-nums leading-none mt-1">
            {total}<span className="text-[#6E6E6E] text-2xl"> / {cap}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="brand-tiny text-[#6E6E6E]">Spots left</div>
          <div className="font-display text-3xl tabular-nums leading-none mt-1 text-[#FF6A00]">{left}</div>
        </div>
      </div>
      <div className="relative h-2 bg-[#F0EDE4] mt-4 border border-[#E5E1D8]">
        <div className="absolute inset-y-0 left-0 bg-[#B9E641]" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-4 text-sm text-[#6E6E6E]">
        More members joining means bigger live moments.
      </p>
      <a href="https://chat.whatsapp.com/IXRiURI5jMaK908CkrDK7B" target="_blank" rel="noopener noreferrer" className="btn-primary mt-5 h-11 px-4 inline-block" data-testid="community-whatsapp">
        Join WhatsApp Community →
      </a>
    </div>
  );
}

/* ------------ Invite card ------------ */
function InviteCard({ memberNo }) {
  const [copied, setCopied] = useState(false);
  const [summary, setSummary] = useState(null);
  const code = `DSL${String(memberNo || 1).padStart(3, "0")}`;
  const shareLink = `${typeof window !== "undefined" ? window.location.origin : "https://dropshop.live"}/?invite=${code}`;

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/my-invites")
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const invitesTotal = summary?.invites_total ?? 3;
  const invitesUsed = summary?.invites_used ?? 0;
  const remaining = summary?.invites_remaining ?? (invitesTotal - invitesUsed);
  const referrals = summary?.referrals ?? [];
  const capped = remaining <= 0;

  const copy = () => {
    if (capped) return;
    navigator.clipboard?.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="border-2 border-[#161616] bg-[#FFFFFF] shadow-[4px_4px_0_#161616] p-6" data-testid="clubhouse-invite">
      <div className="brand-tag mb-2">// Invites · 005</div>
      <div className="font-display text-2xl uppercase tracking-tight leading-tight">
        Invite <span className="text-outline">friends.</span>
      </div>
      <p className="mt-3 text-sm text-[#6E6E6E]">
        {capped ? (
          <>You&rsquo;ve used all <span className="text-[#161616] font-bold">3 invite codes.</span> Thank you for spreading the drop.</>
        ) : (
          <>You have <span className="text-[#161616] font-bold">{remaining} exclusive invite code{remaining === 1 ? "" : "s"}.</span> Invite people who love discovering incredible deals before everyone else.</>
        )}
      </p>
      <div className="mt-5 flex items-baseline justify-between">
        <div>
          <div className="brand-tiny text-[#6E6E6E]">Invites remaining</div>
          <div className="font-display text-4xl tabular-nums leading-none mt-1" data-testid="invite-remaining">
            {remaining}<span className="text-[#6E6E6E] text-2xl"> / {invitesTotal}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="brand-tiny text-[#6E6E6E]">Your code</div>
          <div className="font-mono-ds text-lg font-bold text-[#264733]" data-testid="invite-code">{code}</div>
        </div>
      </div>
      <button
        onClick={copy}
        disabled={capped}
        className={`mt-5 h-11 px-4 w-full ${capped ? "btn-outline-ns opacity-60 cursor-not-allowed" : "btn-urgent"}`}
        data-testid="invite-copy"
      >
        {capped ? "All invites used" : (copied ? "Link copied ✓" : "Invite Friends →")}
      </button>
      {referrals.length > 0 && (
        <ul className="mt-4 space-y-1.5" data-testid="invite-referrals">
          {referrals.map((r, i) => (
            <li key={i} className="flex items-center justify-between border border-[#E5E1D8] bg-[#F8F7F2] px-3 py-2 font-mono-ds text-[11px] uppercase tracking-[0.18em]">
              <span className="text-[#161616]">
                {r.name || r.email || "Guest"}
              </span>
              <span className={`${r.kind === "member" ? "text-[#264733] font-bold" : "text-[#6E6E6E]"}`}>
                {r.kind === "member" ? `#${String(r.member_no || 0).padStart(3, "0")}` : "Waitlist"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------ Invite Impact Ledger (only when referrals > 0) ------------ */
function InviteImpactLedger({ memberNo, foundingCap }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/auth/my-invites")
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const referrals = summary?.referrals ?? [];
  if (referrals.length === 0) return null;

  const foundingRefs = referrals.filter((r) => r.member_no && r.member_no <= (foundingCap || 250));
  const totalCount = referrals.length;
  const contributionPct = ((foundingRefs.length / (foundingCap || 250)) * 100).toFixed(1);
  const yourCode = `DSL${String(memberNo || 1).padStart(3, "0")}`;

  return (
    <section
      className="border-y border-[#161616] bg-[#161616] text-white px-6 md:px-10 py-12 md:py-16"
      data-testid="clubhouse-impact"
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#FF6A00]">// Invite Impact · 008</div>
            <h2 className="mt-2 font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight">
              You brought <span className="text-[#B9E641] tabular-nums" data-testid="impact-count">{totalCount}</span> {totalCount === 1 ? "person" : "people"}
              <br /><span className="italic font-normal text-[#B9E641] tracking-[-0.01em]">into the room.</span>
            </h2>
          </div>
          <div className="font-mono-ds text-[10px] uppercase tracking-widest text-white/50 max-w-[240px] text-right hidden md:block">
            Every friend who signed up with {yourCode} is now part of Drop Shop Live
          </div>
        </div>

        {/* Referral pills */}
        <div className="mt-8 flex gap-2.5 overflow-x-auto pb-3 -mx-6 md:-mx-10 px-6 md:px-10 snap-x scroll-smooth" data-testid="impact-pills">
          {referrals.map((r, i) => {
            const label = r.name || (r.email ? r.email.split("@")[0] : "Guest");
            const isMember = r.kind === "member" && !!r.member_no;
            const isFounding = isMember && r.member_no <= (foundingCap || 250);
            const mnLabel = isMember ? `#${String(r.member_no).padStart(3, "0")}` : "Waitlist";
            return (
              <div
                key={`${r.email || i}-${r.member_no || i}`}
                className={`shrink-0 snap-start border-2 px-4 py-3 min-w-[180px] ${
                  isFounding
                    ? "border-[#B9E641] bg-[#B9E641]/10 shadow-[3px_3px_0_#FF6A00]"
                    : isMember
                      ? "border-white/30 bg-white/[0.04]"
                      : "border-white/15 bg-white/[0.02]"
                }`}
                data-testid={`impact-pill-${i}`}
              >
                <div className="font-mono-ds text-[10px] uppercase tracking-widest text-white/50">
                  {isFounding ? "🌟 Founding" : isMember ? "🚀 Member" : "◇ Waitlist"}
                </div>
                <div className="mt-1 font-display text-lg uppercase tracking-tight leading-tight truncate text-white">
                  {label}
                </div>
                <div className={`mt-0.5 font-mono-ds text-[11px] font-bold tabular-nums ${
                  isFounding ? "text-[#B9E641]" : isMember ? "text-white" : "text-white/60"
                }`}>
                  {mnLabel}
                </div>
              </div>
            );
          })}
        </div>

        {/* Contribution footer */}
        <div className="mt-6 border-t border-white/10 pt-5 flex flex-col md:flex-row md:items-baseline md:justify-between gap-3">
          <div className="font-mono-ds text-[11px] uppercase tracking-widest text-white/60">
            <span className="text-[#FF6A00]">◆</span> Together, you&rsquo;ve helped shape <span className="text-[#B9E641] font-bold tabular-nums">{contributionPct}%</span> of the Founding cohort.
          </div>
          <div className="font-mono-ds text-[10px] uppercase tracking-widest text-white/40">
            Your invite code · <span className="text-[#FF6A00] font-bold">{yourCode}</span>
          </div>
        </div>
      </div>
    </section>
  );
}



/* ------------ Progress step row ------------ */
function ProgressStep({ done, label }) {
  return (
    <li className={`flex items-center gap-4 border p-3.5 ${done ? "border-[#264733] bg-[#B9E641]/20" : "border-[#E5E1D8] bg-[#FFFFFF]"}`}>
      <span className={`w-6 h-6 border flex items-center justify-center font-mono-ds text-xs ${done ? "border-[#264733] bg-[#264733] text-white" : "border-[#161616] bg-[#F8F7F2]"}`}>
        {done ? "✓" : ""}
      </span>
      <span className={`font-display text-base md:text-lg uppercase tracking-tight leading-tight ${done ? "text-[#161616]" : "text-[#6E6E6E]"}`}>
        {label}
      </span>
    </li>
  );
}

/* ------------ FAQ accordion item ------------ */
function FaqItem({ idx, q, a }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-[#161616]" data-testid={`faq-item-${idx}`}>
      <button
        className="w-full flex items-center justify-between gap-4 py-4 text-left"
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-display text-base md:text-xl uppercase tracking-tight leading-tight pr-4">{q}</span>
        <span className={`font-mono-ds text-2xl leading-none text-[#264733] transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && (
        <div className="pb-5 pr-8 text-[#6E6E6E] text-sm md:text-base leading-relaxed" data-testid={`faq-answer-${idx}`}>{a}</div>
      )}
    </div>
  );
}

/* ------------ Notification card (Add-to-Calendar) ------------ */
function NotificationCard({ subscribed, subscribedAt, onSubscribed }) {
  const [busy, setBusy] = useState(false);

  const openGoogle = async () => {
    setBusy(true);
    try {
      await addAllToGoogleCalendar("notify_card");
      if (onSubscribed) await onSubscribed();
    } finally { setBusy(false); }
  };

  const subscribedDate = subscribedAt ? new Date(subscribedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null;

  return (
    <div id="notify-card" className="border-2 border-[#161616] bg-[#FFFFFF] shadow-[4px_4px_0_#161616] p-6 md:p-8" data-testid="clubhouse-notifications">
      <div className="brand-tag mb-2">// Season One Calendar · 008</div>
      <div className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-tight">
        Add to <span className="text-outline">your calendar.</span>
      </div>
      <p className="mt-3 text-sm text-[#6E6E6E]">
        Every drop lands on your Google Calendar with a <span className="text-[#161616] font-semibold">10-minute pre-live reminder</span>. Fifteen daily drops, Aug 15 → 29, 9:00 PM IST.
      </p>

      {subscribed && (
        <div className="mt-5 border border-[#264733] bg-[#B9E641]/40 px-4 py-3 flex items-center justify-between gap-3" data-testid="notify-subscribed-banner">
          <div>
            <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#264733] font-bold">// Added to your calendar</div>
            {subscribedDate && (
              <div className="font-mono-ds text-[11px] text-[#264733] mt-0.5">Subscribed on {subscribedDate}</div>
            )}
          </div>
          <span className="font-display text-2xl leading-none text-[#264733]">✓</span>
        </div>
      )}

      <div className="mt-6">
        <button
          onClick={openGoogle}
          disabled={busy}
          className="btn-primary w-full h-12 px-4 flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="notify-google-calendar"
        >
          <span className="text-lg leading-none">🗓</span>
          <span>Add to Google Calendar</span>
        </button>
      </div>

      <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm text-[#161616]" data-testid="notify-perks">
        {["15 daily events · Aug 15 → 29", "9:00 PM IST · 2 minutes each", "10-minute pre-live reminder", "One tap unsubscribe in Google Calendar"].map((t, i) => (
          <li key={i} className="flex items-center gap-2.5 border border-[#E5E1D8] bg-[#F8F7F2] px-3 py-2.5">
            <span className="w-1.5 h-1.5 bg-[#FF6A00]" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
